import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AppointmentConfig, AppointmentMode, tenantApi } from '../../lib/tenant-api';

interface AppointmentSectionProps { }

const AppointmentSection: React.FC<AppointmentSectionProps> = () => {
  const queryClient = useQueryClient();
  const enableAppointmentMode = false; // Enable when we're ready to release the feature

  // State for form data
  const [isEnabled, setIsEnabled] = useState(false);
  const [appointmentMode, setAppointmentMode] = useState<AppointmentMode>('request_only');
  const [contactEmail, setContactEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [newService, setNewService] = useState('');

  // Fetch appointment config
  const { data: config, isLoading } = useQuery<AppointmentConfig | null, Error>({
    queryKey: ['appointmentConfig'],
    queryFn: tenantApi.getAppointmentConfig,
  });

  // Toggle appointment config mutation
  const toggleAppointmentMutation = useMutation({
    mutationFn: tenantApi.toggleAppointmentConfig,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['appointmentConfig'] });
      const action = data.is_active ? 'enabled' : 'disabled';
      toast.success(`Appointment booking ${action} successfully!`);

      // Update local state with the response data
      if (data.is_active) {
        setIsEnabled(true);
        setAppointmentMode(data.appointment_mode);
        setContactEmail(data.contact_email || '');
        setPhoneNumber(data.phone_number || '');
        setServices(data.services || []);
      } else {
        setIsEnabled(false);
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to toggle appointment settings: ${error.message}`);
    },
  });

  // Update mutation
  const updateConfigMutation = useMutation({
    mutationFn: tenantApi.updateAppointmentConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointmentConfig'] });
      toast.success('Appointment settings updated successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (config) {
      setIsEnabled(config.is_active);
      setAppointmentMode(config.appointment_mode);
      setContactEmail(config.contact_email || '');
      setPhoneNumber(config.phone_number || '');
      setServices(config.services || []);
    }
  }, [config]);

  // Handle toggle change
  const handleToggleChange = (enabled: boolean) => {
    setIsEnabled(enabled);
    toggleAppointmentMutation.mutate({ enabled });
  };

  const handleAddService = () => {
    const trimmedService = newService.trim();
    if (trimmedService && !services.includes(trimmedService)) {
      setServices([...services, trimmedService]);
      setNewService('');
    }
  };

  const handleRemoveService = (serviceToRemove: string) => {
    setServices(services.filter(service => service !== serviceToRemove));
  };

  const handleServiceInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddService();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload: any = {
      appointment_mode: appointmentMode,
      services: services,
    };

    if (contactEmail.trim()) {
      payload.contact_email = contactEmail;
    }

    if (phoneNumber.trim()) {
      payload.phone_number = phoneNumber;
    }

    updateConfigMutation.mutate(payload);
  };

  if (isLoading) {
    return <div className="p-6">Loading appointment settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Appointment settings</h3>
        <p className="text-sm text-gray-600 mb-4">
          Allow users to request appointments directly through your AI widget
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 ">
          <div>
            <h4 className="font-medium text-gray-900">Accept appointment scheduling</h4>
            <p className="text-sm text-gray-600">
              Enable appointment requests from users chatting with your AI
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => handleToggleChange(e.target.checked)}
              className="sr-only peer"
              disabled={toggleAppointmentMutation.isPending || updateConfigMutation.isPending}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
        </div>

        {isEnabled && (
          <>
            {/* Appointment Mode */}
            {enableAppointmentMode && (<div>
              <h4 className="font-medium text-gray-900 mb-2">Appointment Mode</h4>
              <p className="text-sm text-gray-600 mb-4">Choose how appointments are handled</p>

              <div className="space-y-3">
                <label className="flex items-start space-x-3 p-3 border border-gray-200  cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="appointmentMode"
                    value="request_only"
                    checked={appointmentMode === 'request_only'}
                    onChange={(e) => setAppointmentMode(e.target.value as 'request_only')}
                    className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    disabled={updateConfigMutation.isPending}
                  />
                  <div>
                    <div className="font-medium text-gray-900">Appointment request only</div>
                    <div className="text-sm text-gray-600">Collect information and send to your email/SMS</div>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-gray-200  cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="appointmentMode"
                    value="auto_schedule"
                    checked={appointmentMode === 'auto_schedule'}
                    onChange={(e) => setAppointmentMode(e.target.value as 'auto_schedule')}
                    className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    disabled={updateConfigMutation.isPending}
                  />
                  <div>
                    <div className="font-medium text-gray-900">Auto-Schedule via PMS</div>
                    <div className="text-sm text-gray-600">Automatically schedule appointments through your PMS integration.</div>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border-2 border-indigo-200 bg-indigo-50  cursor-pointer">
                  <input
                    type="radio"
                    name="appointmentMode"
                    value="both_methods"
                    checked={appointmentMode === 'both_methods'}
                    onChange={(e) => setAppointmentMode(e.target.value as 'both_methods')}
                    className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    disabled={updateConfigMutation.isPending}
                  />
                  <div>
                    <div className="font-medium text-gray-900">Both Methods</div>
                    <div className="text-sm text-gray-600">Support both appointment requests and auto-scheduling based on availability.</div>
                  </div>
                </label>
              </div>
            </div>)}

            {/* Request Details */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">Request Details</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact email
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Email where appointment requests will be sent</p>
                  <input
                    id="contactEmail"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="admin@lawma.com"
                    className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={updateConfigMutation.isPending}
                  />
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone number
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Phone number for SMS notifications</p>
                  <input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 099-0000"
                    className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={updateConfigMutation.isPending}
                  />
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Services
              </label>
              <p className="text-xs text-gray-500 mb-3">Services that customers can book appointments for</p>

              {/* Add Service Input */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  type="text"
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  onKeyPress={handleServiceInputKeyPress}
                  placeholder="Enter service name"
                  className="flex-1 px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={updateConfigMutation.isPending}
                />
                <button
                  type="button"
                  onClick={handleAddService}
                  disabled={!newService.trim() || updateConfigMutation.isPending}
                  className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add Service
                </button>
              </div>

              {/* Services Pills */}
              {services.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {services.map((service, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      {service}
                      <button
                        type="button"
                        onClick={() => handleRemoveService(service)}
                        className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-600 focus:outline-none focus:bg-indigo-200 focus:text-indigo-600"
                        disabled={updateConfigMutation.isPending}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={updateConfigMutation.isPending || !isEnabled}
            className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updateConfigMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentSection;
